import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Truck, FileDown, FileText, PackageCheck, QrCode } from "lucide-react";

const STATUSES = ["commandée", "confirmée", "préparée", "expédiée", "en_livraison", "livrée", "en_attente", "annulée"];
const PIPELINE = ["commandée", "confirmée", "préparée", "expédiée", "en_livraison", "livrée"];

export default function Clients() {
  const { t, lang } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openO, setOpenO] = useState(false);
  const [openD, setOpenD] = useState(false);
  const [openPr, setOpenPr] = useState(false);
  const [newC, setNewC] = useState({ name: "", type: "", city: "", phone: "", email: "" });
  const [tab, setTab] = useState("orders");
  const [newO, setNewO] = useState({ customer_id: "", product_id: "", quantity: "", destination: "", status: "en_attente" });
  const [newD, setNewD] = useState({ name: "", region: "" });
  const [newPr, setNewPr] = useState({ name: "", unit: "", price: "" });

  async function loadAll() {
    const [c, o, p, d] = await Promise.all([
      api.get("/customers"), api.get("/orders"), api.get("/products"), api.get("/destinations"),
    ]);
    setCustomers(c.data); setOrders(o.data); setProducts(p.data); setDestinations(d.data);
  }
  useEffect(() => { loadAll(); }, []);

  // Pré-remplissage via l'assistant IA (action create_customer)
  useEffect(() => {
    const raw = sessionStorage.getItem("ai_prefill");
    if (!raw) return;
    try {
      const a = JSON.parse(raw);
      if (a.type === "create_customer") {
        setNewC((prev) => ({ ...prev, name: a.name || "", phone: a.phone || "", city: a.city || "", email: a.email || "" }));
        setTab("customers");
        setOpenC(true);
        toast.success(t("ai_prefilled"));
      }
    } catch { /* ignore */ }
    sessionStorage.removeItem("ai_prefill");
  }, []);

  async function addCustomer() {
    if (!newC.name) return toast.error("Nom requis");
    await api.post("/customers", newC);
    setOpenC(false); setNewC({ name: "", type: "", city: "", phone: "", email: "" });
    toast.success(t("added")); loadAll();
  }
  async function addOrder() {
    const cust = customers.find((c) => c.id === newO.customer_id);
    const prod = products.find((p) => p.id === newO.product_id);
    if (!cust || !prod) return toast.error("Client & produit requis");
    const qty = parseFloat(newO.quantity || 0);
    await api.post("/orders", {
      ...newO, customer_name: cust.name, product_name: prod.name,
      quantity: qty, unit_price: prod.price, total: qty * (prod.price || 0),
      order_date: new Date().toISOString(),
    });
    setOpenO(false); setNewO({ customer_id: "", product_id: "", quantity: "", destination: "", status: "en_attente" });
    toast.success(t("added")); loadAll();
  }
  async function addDest() {
    if (!newD.name) return;
    await api.post("/destinations", newD);
    setOpenD(false); setNewD({ name: "", region: "" });
    toast.success(t("added")); loadAll();
  }
  async function addProduct() {
    if (!newPr.name) return;
    await api.post("/products", { ...newPr, price: parseFloat(newPr.price || 0) });
    setOpenPr(false); setNewPr({ name: "", unit: "", price: "" });
    toast.success(t("added")); loadAll();
  }
  async function updateStatus(id, status) {
    await api.put(`/orders/${id}`, { status });
    toast.success(t("updated")); loadAll();
  }
  async function delOrder(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/orders/${id}`); toast.success(t("deleted")); loadAll();
  }
  async function delCustomer(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/customers/${id}`); toast.success(t("deleted")); loadAll();
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div>
        <div className="label-eyebrow mb-2">Module 4</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("clients")}</h1>
        <p className="text-muted-foreground mt-1">{customers.length} clients · {orders.length} commandes · {destinations.length} destinations</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-orders">{t("orders")}</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">{customers.length} {t("clients")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produits</TabsTrigger>
          <TabsTrigger value="destinations" data-testid="tab-destinations">Destinations</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openO} onOpenChange={setOpenO}>
              <DialogTrigger asChild><Button data-testid="add-order-btn"><Plus className="w-4 h-4 mr-2" />{t("orders")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — {t("orders")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>{t("customer")}</Label>
                    <Select value={newO.customer_id} onValueChange={(v) => setNewO({ ...newO, customer_id: v })}>
                      <SelectTrigger data-testid="order-customer"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("product")}</Label>
                    <Select value={newO.product_id} onValueChange={(v) => setNewO({ ...newO, product_id: v })}>
                      <SelectTrigger data-testid="order-product"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatXAF(p.price)}/{p.unit}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("quantity")}</Label><Input type="number" value={newO.quantity} onChange={(e) => setNewO({ ...newO, quantity: e.target.value })} data-testid="order-qty" /></div>
                  <div>
                    <Label>{t("destination")}</Label>
                    <Select value={newO.destination} onValueChange={(v) => setNewO({ ...newO, destination: v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{destinations.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={addOrder} data-testid="order-save">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 overflow-x-auto" data-testid="order-pipeline">
                {PIPELINE.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="whitespace-nowrap text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary">{t(s) || s}</span>
                    {i < PIPELINE.length - 1 && <span className="text-muted-foreground">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border bg-secondary/50">
                  <tr>
                    <th className="p-3">{t("customer")}</th><th>{t("product")}</th>
                    <th>{t("quantity")}</th><th>{t("total")}</th>
                    <th>{t("destination")}</th><th>{t("status")}</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-border/60 last:border-0">
                      <td className="p-3 font-medium">{o.customer_name}</td>
                      <td>{o.product_name}</td>
                      <td>{o.quantity}</td>
                      <td className="font-semibold">{formatXAF(o.total)}</td>
                      <td><span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" />{o.destination}</span></td>
                      <td>
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(s) || s}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="whitespace-nowrap">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/invoice/${o.id}`} target="_blank" rel="noopener noreferrer" title={t("invoice")}>
                          <Button variant="ghost" size="sm" data-testid={`invoice-${o.id}`}><FileDown className="w-4 h-4" /></Button>
                        </a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/quote/${o.id}`} target="_blank" rel="noopener noreferrer" title={t("quote")}>
                          <Button variant="ghost" size="sm" data-testid={`quote-${o.id}`}><FileText className="w-4 h-4" /></Button>
                        </a>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/delivery-note/${o.id}`} target="_blank" rel="noopener noreferrer" title={t("delivery_note")}>
                          <Button variant="ghost" size="sm" data-testid={`delivery-note-${o.id}`}><PackageCheck className="w-4 h-4" /></Button>
                        </a>
                        {o.ref && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/qr.png`} target="_blank" rel="noopener noreferrer" title={t("qr_code")}>
                            <Button variant="ghost" size="sm" data-testid={`qr-${o.id}`}><QrCode className="w-4 h-4" /></Button>
                          </a>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => delOrder(o.id)}><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openC} onOpenChange={setOpenC}>
              <DialogTrigger asChild><Button data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-2" />{t("customer")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — {t("customer")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="customer-name" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} /></div>
                  <div><Label>{t("type")}</Label><Input placeholder="Grossiste, Hôtel…" value={newC.type} onChange={(e) => setNewC({ ...newC, type: e.target.value })} /></div>
                  <div><Label>{t("city")}</Label><Input value={newC.city} onChange={(e) => setNewC({ ...newC, city: e.target.value })} /></div>
                  <div><Label>{t("phone")}</Label><Input value={newC.phone} onChange={(e) => setNewC({ ...newC, phone: e.target.value })} /></div>
                  <div><Label>{t("email")}</Label><Input value={newC.email} onChange={(e) => setNewC({ ...newC, email: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={addCustomer} data-testid="customer-save">{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {customers.map((c) => (
              <Card key={c.id} className="card-hover border-border">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="label-eyebrow">{c.type || "—"}</div>
                      <div className="text-lg font-semibold mt-1">{c.name}</div>
                    </div>
                    <Badge variant="secondary">{c.city}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-2 space-y-0.5">
                    <div>{c.phone}</div>
                    <div>{c.email || "—"}</div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => delCustomer(c.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openPr} onOpenChange={setOpenPr}>
              <DialogTrigger asChild><Button data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" />Produit</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — Produit</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{t("name")}</Label><Input value={newPr.name} onChange={(e) => setNewPr({ ...newPr, name: e.target.value })} /></div>
                  <div><Label>{t("unit")}</Label><Input value={newPr.unit} onChange={(e) => setNewPr({ ...newPr, unit: e.target.value })} /></div>
                  <div><Label>{t("price")} (FCFA)</Label><Input type="number" value={newPr.price} onChange={(e) => setNewPr({ ...newPr, price: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={addProduct}>{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger">
            {products.map((p) => (
              <Card key={p.id} className="card-hover border-border">
                <CardContent className="p-4">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">/ {p.unit}</div>
                  <div className="text-lg font-bold text-primary mt-2">{formatXAF(p.price)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="destinations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openD} onOpenChange={setOpenD}>
              <DialogTrigger asChild><Button data-testid="add-dest-btn"><Plus className="w-4 h-4 mr-2" />Destination</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add")} — Destination</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("name")}</Label><Input value={newD.name} onChange={(e) => setNewD({ ...newD, name: e.target.value })} /></div>
                  <div><Label>Région</Label><Input value={newD.region} onChange={(e) => setNewD({ ...newD, region: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={addDest}>{t("save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {destinations.map((d) => (
              <Card key={d.id} className="card-hover border-border">
                <CardContent className="p-4">
                  <div className="text-lg font-semibold">{d.name}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest">{d.region}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
